import { CancelTokenSource } from 'axios';
import { useEffect, useRef, useState } from 'react';

import { AuthToken } from '@services/auth-token/authToken';
import { generateNewCancelTokenSource } from '@services/axios/axiosUtils';
import { BackOfficeAPIClient } from '@services/axios/clients/backOfficeAPIClient';
import { BillingDataPayload, instanceOfBillingData } from '@shared/interfaces/backoffice/billingData';

export default function useBillingData({ fetchOnInit = true } = {}) {
    const [isLoading, setIsLoading]     = useState(false);
    const [hasError, setHasError]       = useState(false);
    const [billingData, setBillingData] = useState<BillingDataPayload>(null);
    const tokenSource                   = useRef<CancelTokenSource>(null);

    useEffect(() => {
        if (fetchOnInit) {
            fetch();
        }
    }, []);

    const fetch = async () => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.getBillingData<BillingDataPayload>(clientId, {
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setBillingData(response.data);
                setHasError(false);

                return response.data;
            }

            throw new Error('Failed to fetch billing data!');
        } catch (e) {
            console.error(e);
            setHasError(true);

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const update = async (payload: Omit<BillingDataPayload, 'clientId'>) => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.updateBillingData<BillingDataPayload>({
                clientId,
                ...payload,
            }, {
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setBillingData(response.data);
                setHasError(false);
                setIsLoading(false);

                return true;
            }

            throw new Error('Failed to fetch billing data!');
        } catch (e) {
            console.error(e);
            setHasError(true);
            setIsLoading(false);

            return false;
        }
    };

    const hasValidBillingData = (billingData: BillingDataPayload) => {
        if (
            instanceOfBillingData(billingData)
            && !!billingData.clientId
            && !!billingData.email
            && !!billingData.companyName
            && !!billingData.billingEmail
            && !!billingData.country
            && !!billingData.city
            && !!billingData.address
            && !!billingData.postalCode
            && !!billingData.businessTaxType
            && !!billingData.businessTaxId
        ) {
            return true;
        }

        return false;
    };

    return {
        isLoading,
        hasError,
        billingData,
        hasValidBillingData,
        fetch,
        update,
    };
}
